/**
 * Mapa papel -> permissões. Espelho exato da matriz do documento 03.
 * No monorepo final isto vive em `shared` e e importado pelo backend tambem,
 * para UI e API nunca divergirem (documento 17.1).
 */

export type AppCode = 'MKT' | 'CRM' | 'FLW' | 'BIO' | 'POP' | 'MAIL';
export type Role = 'owner' | 'admin' | 'manager' | 'member' | 'agent' | 'viewer' | 'correio';

export const PERMISSIONS = [
  // plataforma
  'platform.member:manage',
  'platform.billing:manage',
  'platform.apikey:manage',
  'platform.app:install',
  'platform.audit:read',
  // MKT
  'mkt.contact:read',
  'mkt.contact:create',
  'mkt.contact:update',
  'mkt.contact:delete',
  'mkt.landingpage:read',
  'mkt.landingpage:create',
  'mkt.landingpage:update',
  'mkt.automation:read',
  'mkt.automation:create',
  'mkt.automation:update',
  'mkt.email:send',
  'mkt.report:read',
  // CRM
  'crm.pipeline:manage',
  'crm.deal:read',
  'crm.deal:create',
  'crm.deal:update',
  'crm.deal:delete',
  'crm.deal:reassign',
  'crm.automation:manage',
  'crm.report:read',
  // FLW
  'flw.conversation:handle',
  'flw.chatbot:manage',
  'flw.channel:connect',
  'flw.campaign:manage',
  'flw.report:read',
  // Painel de atendimento e fila inteira nos Relatórios, para quem supervisiona
  // sem administrar canal/chatbot/campanha (ROTAS-FLOW.md §1 e §4: "STAFF
  // supervisor" é o atendente com esta permissão extra, não um papel à parte).
  'flw.queue:supervise',
  // BIO
  'bio.site:read',
  'bio.site:create',
  'bio.site:update',
  'bio.site:publish',
  'bio.site:delete',
  'bio.product:manage',
  'bio.order:read',
  'bio.order:manage',
  'bio.quiz:manage',
  'bio.booking:manage',
  'bio.report:read',
  // POP · Flow Pop-up
  'pop.widget:manage',
  'pop.conversation:handle',
  'pop.report:read',
  // MAIL · Portal (e-mail, documentos, agenda, vídeo)
  'mail.message:read',
  'mail.message:send',
  'mail.account:manage',
  'mail.document:read',
  'mail.document:manage',
  'mail.meeting:read',
  'mail.meeting:manage',
  'mail.schedule:manage',
] as const;

export type Permission = (typeof PERMISSIONS)[number];

const MKT_OPERATE: Permission[] = [
  'mkt.contact:read',
  'mkt.contact:create',
  'mkt.contact:update',
  'mkt.landingpage:read',
  'mkt.landingpage:create',
  'mkt.landingpage:update',
  'mkt.automation:read',
  'mkt.automation:create',
  'mkt.email:send',
  'mkt.report:read',
];

const CRM_OPERATE: Permission[] = [
  'crm.deal:read',
  'crm.deal:create',
  'crm.deal:update',
  'crm.report:read',
];

const CRM_MANAGE: Permission[] = [
  ...CRM_OPERATE,
  'crm.pipeline:manage',
  'crm.deal:delete',
  'crm.deal:reassign',
  'crm.automation:manage',
];

const FLW_OPERATE: Permission[] = ['flw.conversation:handle', 'flw.report:read'];

const FLW_MANAGE: Permission[] = [...FLW_OPERATE, 'flw.chatbot:manage', 'flw.campaign:manage', 'flw.queue:supervise'];

// Editar e publicar são coisas diferentes: publicar é o ato que expõe a marca
// do cliente ao público, então fica um degrau acima (doc 04.3).
const BIO_OPERATE: Permission[] = [
  'bio.site:read',
  'bio.site:update',
  'bio.quiz:manage',
  'bio.booking:manage',
  'bio.report:read',
];

const BIO_MANAGE: Permission[] = [
  ...BIO_OPERATE,
  'bio.site:create',
  'bio.site:publish',
  'bio.product:manage',
  'bio.order:read',
  'bio.order:manage',
];

// Quem opera o pop-up atende e mede; quem o configura decide o que o site
// inteiro do cliente diz a quem chega, e isso é um degrau acima, como publicar
// no Bio.
const POP_OPERATE: Permission[] = ['pop.conversation:handle', 'pop.report:read'];

const POP_MANAGE: Permission[] = [...POP_OPERATE, 'pop.widget:manage'];

// Ler a caixa e responder é o trabalho do dia. Configurar CONTA é outra coisa:
// é onde a senha do IMAP do cliente é digitada, e quem pode fazer isso pode
// ler tudo que entra e sair como se fosse ele. Fica com admin, junto de
// `platform.member:manage`, pelo mesmo motivo.
const MAIL_OPERATE: Permission[] = [
  'mail.message:read',
  'mail.message:send',
  'mail.document:read',
  'mail.meeting:read',
];

const MAIL_MANAGE: Permission[] = [
  ...MAIL_OPERATE,
  'mail.document:manage',
  'mail.meeting:manage',
  'mail.schedule:manage',
];

const MANAGER: Permission[] = [
  ...MKT_OPERATE,
  'mkt.contact:delete',
  'mkt.automation:update',
  ...CRM_MANAGE,
  ...FLW_MANAGE,
  ...BIO_MANAGE,
  ...POP_MANAGE,
  ...MAIL_MANAGE,
];

const ADMIN: Permission[] = [
  ...MANAGER,
  'platform.member:manage',
  'platform.apikey:manage',
  'platform.app:install',
  'platform.audit:read',
  'flw.channel:connect',
  'bio.site:delete',
  'mail.account:manage',
];

export const ROLE_GRANTS: Record<Role, Permission[]> = {
  owner: [...ADMIN, 'platform.billing:manage'],
  admin: ADMIN,
  manager: MANAGER,
  member: [
    ...MKT_OPERATE,
    ...CRM_OPERATE,
    'flw.conversation:handle',
    ...BIO_OPERATE,
    ...POP_OPERATE,
    ...MAIL_OPERATE,
  ],
  // correio: o papel que a entrada pelo webmail dá a quem ainda não é membro
  // (0147). Só e-mail, sem o resto da operação. Fica fora de `PAPEIS`: não se
  // convida ninguém como correio, quem recebe o papel é a própria caixa.
  correio: ['mail.message:read', 'mail.message:send', 'mail.document:read', 'mail.meeting:read'],
  // agent e especializado: atendimento e relatório próprio, sem estrutura (doc 03.2).
  agent: ['flw.conversation:handle', 'flw.report:read', ...POP_OPERATE],
  viewer: [
    'mkt.contact:read',
    'mkt.landingpage:read',
    'mkt.automation:read',
    'mkt.report:read',
    'crm.deal:read',
    'crm.report:read',
    'bio.site:read',
    'bio.report:read',
    'pop.report:read',
    'mail.document:read',
    'mail.meeting:read',
  ],
};

export function resolveGrants(role: Role): Permission[] {
  return ROLE_GRANTS[role] ?? [];
}

export const APPS: AppCode[] = ['MKT', 'CRM', 'FLW', 'BIO', 'POP', 'MAIL'];


/**
 * Rótulo de cada papel, em um lugar só — console, galeria e qualquer seletor
 * de papel puxam daqui, para nunca divergirem entre si (mesmo raciocínio do
 * `NOME_APP` acima).
 */
export const PAPEIS: { value: Role; label: string }[] = [
  { value: 'owner', label: 'Dono' },
  { value: 'admin', label: 'Administrador' },
  { value: 'manager', label: 'Gerente' },
  { value: 'member', label: 'Membro' },
  { value: 'agent', label: 'Atendente' },
  { value: 'viewer', label: 'Leitor' },
];

/** Nome comercial de cada produto. Um lugar só, para console e painel não divergirem. */
export const NOME_APP: Record<AppCode, string> = {
  MKT: 'Marketing',
  CRM: 'CRM',
  FLW: 'Flow',
  BIO: 'Bio',
  POP: 'Flow Pop-up',
  MAIL: 'Portal',
};

export function appOf(permission: Permission): AppCode | 'PLATFORM' {
  const prefix = permission.split('.')[0]?.toUpperCase();
  return (APPS as string[]).includes(prefix ?? '') ? (prefix as AppCode) : 'PLATFORM';
}

/** Apps que o papel consegue operar; alimenta o nav rail (doc 17.6). */
export function appsForRole(role: Role): AppCode[] {
  const grants = resolveGrants(role);
  return APPS.filter((app) => grants.some((p) => appOf(p) === app));
}

/* ---------------- CRM: o que a tela deixa fazer ---------------- */

/**
 * Mesma régua de `useCan` (`core/auth.tsx`), em função pura: a permissão só
 * vale se o app dela estiver instalado na organização. Existe aqui para as
 * regras do CRM abaixo poderem ser testadas sem montar o provedor de sessão.
 */
export function podeNoApp(grants: readonly Permission[], instalados: readonly AppCode[], p: Permission): boolean {
  const app = p.split('.')[0]?.toUpperCase();
  if (app !== 'PLATFORM' && !instalados.includes(app as AppCode)) return false;
  return grants.includes(p);
}

/** Papéis que enxergam todos os negócios de fábrica (ROTAS-CRM.md, seção 1 e 4). */
export const GESTORES_CRM: readonly string[] = ['owner', 'admin', 'manager'];

/**
 * As permissões que cada controle do CRM consulta, num lugar só. Espelho das
 * políticas de escrita da `0109_crm_permissoes_empresas_e_automacoes.sql`:
 * se a tela mostrar um botão que a RLS recusa, a pessoa clica e recebe erro;
 * se esconder um que a RLS aceita, a função some sem motivo. Os dois lados
 * leem a mesma matriz (`ROLE_GRANTS` aqui, `role_permissions` lá).
 */
export type RegrasCrm = {
  criarNegocio: boolean;
  editarNegocio: boolean;
  excluirNegocio: boolean;
  reatribuir: boolean;
  gerenciarFunil: boolean;
  gerenciarAutomacao: boolean;
  criarContato: boolean;
  editarContato: boolean;
  criarTarefa: boolean;
  editarTarefa: boolean;
};

export function regrasDoCrm(pode: (p: Permission) => boolean): RegrasCrm {
  const editarNegocio = pode('crm.deal:update');
  return {
    criarNegocio: pode('crm.deal:create'),
    editarNegocio,
    excluirNegocio: pode('crm.deal:delete'),
    reatribuir: pode('crm.deal:reassign'),
    gerenciarFunil: pode('crm.pipeline:manage'),
    gerenciarAutomacao: pode('crm.automation:manage'),
    // Contato é compartilhado com o Marketing (políticas `contacts_*`).
    criarContato: pode('mkt.contact:create') || pode('crm.deal:create'),
    editarContato: pode('mkt.contact:update') || editarNegocio,
    criarTarefa: pode('crm.deal:create') || editarNegocio,
    editarTarefa: editarNegocio,
  };
}

/**
 * `podeMover` da tela, e a política `deals_update` do banco: editar o
 * negócio exige `crm.deal:update` E (poder reatribuir OU o negócio ser seu OU
 * estar sem responsável). STAFF mexe no que é dele; gestor mexe em tudo.
 */
export function podeMexerNoNegocio(
  regras: Pick<RegrasCrm, 'editarNegocio' | 'reatribuir'>,
  userId: string | null | undefined,
  ownerId: string | null | undefined,
): boolean {
  if (!regras.editarNegocio) return false;
  return regras.reatribuir || !ownerId || (!!userId && ownerId === userId);
}

/** Concluir/editar tarefa: quem edita negócio, ou o responsável por ela (`tasks_update`). */
export function podeMexerNaTarefa(
  regras: Pick<RegrasCrm, 'editarTarefa'>,
  userId: string | null | undefined,
  ownerId: string | null | undefined,
): boolean {
  return regras.editarTarefa || (!!userId && ownerId === userId);
}

/** Quem vê todos os negócios: gestor de fábrica, ou papel liberado em `#config` > Equipe. */
export function veTodosOsNegocios(role: string | null | undefined, papeisLiberados: readonly string[]): boolean {
  if (!role) return false;
  return GESTORES_CRM.includes(role) || papeisLiberados.includes(role);
}
